<template>
  <div v-if="showProductOptions" class="flex flex-row flex-wrap text-sm lg:text-base gap-2">
    <component
      :is="item.variant.isActive ? 'span' : 'button'"
      v-for="item in visibleOptions"
      :key="`${option.name} + ${item.option}`"
      :type="item.variant.isActive ? null : 'button'"
      class="shrink-0 box-border flex flex-row items-center cursor-pointer last:mr-0 relative rounded-full overflow-hidden border border-black/15"
      :class="{
        'ring-2 ring-br-green border-transparent': item.variant.isActive,
      }"
      @click="selectVariant(item.variant)"
    >
      <span
        v-if="option && ['Size', 'Material', 'Style'].includes(option.name)"
        data-test-id="textOptions"
        class="px-3 py-1 cursor-pointer relative flex items-center gap-1"
        :class="[
          {
            'text-br-green': item.variant?.isActive,
            'text-black/50 bg-lighter-gray/50 pointer-events-none decoration-br-lighter-gray decoration-2':
              item.variant && item.variant.available === false,
          },
        ]"
      >
        <span class="font-light leading-tight whitespace-nowrap">{{ item.option }}</span>

        <ProductOptionPriceDifference
          v-if="shouldShowPriceDifferenceLabel && !item.variant?.isActive"
          v-bind="{
            selectedPrice: selectedVariant?.price,
            optionPrice: item.variant?.price,
          }"
        >
          <span class="text-black/40 text-xs leading-none mr-1">|</span>
          <span>+</span>
        </ProductOptionPriceDifference>

        <div v-if="item.variant.available === false" class="striped-background absolute inset-0"></div>
      </span>

      <CldnImg
        v-else-if="item.variant && (item.variant.swatch || item.variant.image)"
        :src="item.variant.swatch || item.variant.image.src"
        :alt="item.variant.title"
        crop="fill"
        sizes="32px"
        class="box-content"
        data-test-id="swatch"
        :class="[swatchImgClass]"
      />
    </component>

    <div v-if="hasMoreOptions" class="fluid-xxs uppercase tracking-wide leading-tight font-medium">
      +{{ option?.values.length - maxSwatches }}
    </div>
  </div>
</template>

<script setup>
import CldnImg from '@/components/CldnImg';
import ProductOptionPriceDifference from './ProductOptionPriceDifference.vue';

const props = defineProps({
  option: {
    type: Object,
    default: () => {},
  },

  selectedVariant: {
    type: Object,
    default: () => {},
  },

  variants: {
    type: Array,
    default: () => [],
    required: true,
  },

  maxSwatches: {
    type: Number,
    default: 10,
  },

  swatches: {
    type: Object,
    default: () => {},
  },

  swatchImgClass: {
    type: String,
    default: '',
  },

  alwaysShow: {
    type: Boolean,
    default: false,
  },

  showVariantPriceDifference: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['clickOption']);

const shouldShowPriceDifferenceLabel = computed(() => {
  return props.showVariantPriceDifference && props.option?.name === 'Size';
});

const visibleOptions = computed(() => {
  const mappedOptions = props.option?.values.map((option) => {
    const params = {};

    props.selectedVariant?.selectedOptions?.forEach((option) => {
      params[option.name] = option.value;
    });

    params[props.option.name] = option;

    // find variant that matches the selected params
    const variant = props.variants.find((variant) => {
      return variant.selectedOptions?.every((option) => {
        return params[option.name] === option.value;
      });
    });

    if (!variant) {
      return { option, variant: null };
    }

    const swatch = props.swatches && props.swatches[variant.sku];

    return {
      option,
      variant: { ...variant, swatch, isActive: variant.id === props.selectedVariant.id },
    };
  });

  return (
    mappedOptions
      // filter out null variants
      ?.filter((item) => {
        return item.variant;
      })
      // respect maxSwatches
      ?.slice(0, props.maxSwatches) ?? []
  );
});

const showProductOptions = computed(() => {
  return visibleOptions.value.length > 1 || props.alwaysShow;
});

const hasMoreOptions = computed(() => {
  return props.maxSwatches > 0 && props.option?.values.length > props.maxSwatches;
});

const selectByImage = (image) => {
  const selected = visibleOptions.value.find((item) => {
    return item.variant.image.id === image.id;
  });

  if (selected) {
    selectVariant(selected.variant);
  }
};

const selectVariant = (variant) => {
  emit('clickOption', variant);
};

defineExpose({
  selectByImage,
});
</script>

<style scoped>
.striped-background {
  background: linear-gradient(
    163deg,
    rgba(100, 100, 100, 0) 0%,
    rgba(100, 100, 100, 0) 46%,
    rgba(160, 160, 160, 1) 48%,
    rgba(160, 160, 160, 1) 52%,
    rgba(100, 100, 100, 0) 54%
  );
}
</style>
